import React, { useEffect, useMemo, useState } from 'react'

import { Pagination } from '@material-ui/lab'
import {
    CircularProgress,
    TableSortLabel,
    useTheme,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Box,
} from '@material-ui/core'

import { Container } from '../../components'
import {
    ActionItemAnalytic,
    useEZFolioWorkorderAnalytics,
} from '../../hooks/useEZFolioWorkorderAnalytics'
import { useDateRange, usePagination, useUser } from '../../hooks'
import { WorkorderType } from '../../models'
import { WorkspaceProjectsRow } from './Projects/WorkspaceProjectsRow'
import {
    DateFilter,
    DateFilterButtons,
    LAST_MONTH,
    THIS_MONTH,
} from '../../components/DateFilterButtons'
import { getPreviousMonthDates } from '../../helpers'
import { SearchField } from '../../components/SearchField'
import { ServiceRequestAnalyticModal } from '../../components/ServiceRequestAnalytics/ServiceRequestAnalyticModal'
import { ActionItemAnalyticModal } from '../../components/ActionItemAnalytics/ActionItemAnalyticModal'
import { DateTime } from 'luxon'
import { useTimezone } from '../../contexts/timezone'
import { useUserTags } from '../../contexts/UserTagsContext'
import { typographyStyle } from '../../theme/typographyStyle'

interface Props {
    workorderType: WorkorderType
}

export const ActionItemPage = (props: Props) => {
    const { workorderType } = props

    const [orderBy, setOrderBy] = useState('')
    const [selectedTagIds, setSelectedTagIds] = useState<number[]>([])
    const [dateFilter, setDateFilter] = useState<DateFilter>(THIS_MONTH)
    const [searchText, setSearchText] = useState('')
    const [openDetailModal, setOpenDetailModal] = useState(false)
    const [
        activeActionItemAnalytic,
        setActiveActionItemAnalytic,
    ] = useState<ActionItemAnalytic | null>(null)

    const { organization } = useUser()
    const { currentTimezone } = useTimezone()
    const {
        actionItemAnalytics,
        loadingState,
        serviceRequestDetailAnalytics,
        actionItemDetailAnalytics,
        serviceRequestCommonIssues,
        getActionItemAnalytics,
        setActionItemAnalytics,
        getServiceRequestDetailAnalytics,
        getServiceRequestCommonIssues,
        getActionItemDetailAnalytics,
    } = useEZFolioWorkorderAnalytics()

    const { userTags, loading: tagsLoading } = useUserTags()

    // Helper function to render tags
    const renderTags = (actionItemAnalytic: ActionItemAnalytic) => {
        const relevantTags = userTags.filter((tag) =>
            tag.apartments.includes(actionItemAnalytic.id),
        )

        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    flexWrap: 'wrap',
                }}
            >
                {relevantTags.map((tag) => (
                    <Chip
                        key={tag.id}
                        label={tag.name}
                        size="small"
                        style={{
                            backgroundColor: tag.color,
                            color: 'white',
                            fontSize: '0.7rem',
                            height: '20px',
                        }}
                    />
                ))}
            </div>
        )
    }

    const filteredActionItemAnalytics = useMemo(() => {
        let filtered = actionItemAnalytics?.filter((actionItemAnalytic) => {
            return actionItemAnalytic.apartment_name
                .toLowerCase()
                .includes(searchText.toLowerCase())
        })

        // Apply tag filtering if tags are selected
        if (selectedTagIds.length > 0 && filtered) {
            filtered = filtered.filter((actionItemAnalytic) => {
                // Find tags that include this apartment (using apartment_id from the analytic)
                const relevantTags = userTags.filter((tag) =>
                    tag.apartments.includes(actionItemAnalytic.id),
                )

                // Check if any of the relevant tags are in the selected tags
                return relevantTags.some((tag) =>
                    selectedTagIds.includes(tag.id),
                )
            })
        }

        return filtered
    }, [actionItemAnalytics, searchText, selectedTagIds, userTags])

    const { page, setPage, pageData, numPages, start, end } = usePagination(
        filteredActionItemAnalytics ?? [],
    )

    const today = DateTime.now().setZone(currentTimezone)
    const firstDayOfMonth = today.startOf('month')

    const { dateRange, setStartDate, setEndDate } = useDateRange({
        rangeKey: 'actionItemDateRange',
        initialStartDate: firstDayOfMonth.isValid ? firstDayOfMonth : undefined,
        initialEndDate: today.isValid ? today : undefined,
        timezone: currentTimezone,
    })

    const theme = useTheme()

    useEffect(() => {
        let lowerBoundDate = dateRange.startDate
            ? dateRange.startDate.toISOString()
            : ''
        let upperBoundDate = dateRange.endDate
            ? dateRange.endDate.toISOString()
            : ''

        if (dateFilter === THIS_MONTH) {
            lowerBoundDate = firstDayOfMonth.toISO() ?? ''
            upperBoundDate = today.toISO() ?? ''
        } else if (dateFilter === LAST_MONTH) {
            const { startDate, endDate } = getPreviousMonthDates()
            lowerBoundDate = startDate.toISOString()
            upperBoundDate = endDate.toISOString()
        }

        getActionItemAnalytics({
            organization: organization ?? undefined,
            workorder_type: workorderType,
            start_date: lowerBoundDate,
            end_date: upperBoundDate,
        }).then((actionItemAnalytics) => {
            // Sort by current open count in descending order when data loads
            setOrderBy(CurrentOpenCountDESC)
            const sortedAnalytics = [...actionItemAnalytics].sort(
                (a, b) => b.current_open_count - a.current_open_count,
            )
            setActionItemAnalytics(sortedAnalytics)
        })
    }, [organization, dateFilter])

    const headCellStyle: React.CSSProperties = {
        ...typographyStyle(theme.typography.h6),
        fontWeight: 600,
        color: theme.palette.darkGreen.main,
        justifyContent: 'flex-start',
        flex: 1,
    }

    const getDates = () => {
        let lowerBoundDate = dateRange.startDate
            ? dateRange.startDate.toISOString()
            : ''
        let upperBoundDate = dateRange.endDate
            ? dateRange.endDate.toISOString()
            : ''

        if (dateFilter === THIS_MONTH) {
            lowerBoundDate = firstDayOfMonth
                ? firstDayOfMonth.toISO() ?? ''
                : ''
            upperBoundDate = today ? today.toISO() ?? '' : ''
        } else if (dateFilter === LAST_MONTH) {
            const { startDate, endDate } = getPreviousMonthDates()
            lowerBoundDate = startDate.toISOString()
            upperBoundDate = endDate.toISOString()
        }

        return { lowerBoundDate, upperBoundDate }
    }

    const onClickRow = (actionItemAnalytic: ActionItemAnalytic) => {
        setActiveActionItemAnalytic(actionItemAnalytic)
        setOpenDetailModal(true)

        const { lowerBoundDate, upperBoundDate } = getDates()

        if (workorderType === WorkorderType.SERVICE_REQUEST) {
            getServiceRequestDetailAnalytics({
                params: {
                    apartment_id: actionItemAnalytic.id,
                    upper_bound_date: upperBoundDate,
                    lower_bound_date: lowerBoundDate,
                },
            })

            getServiceRequestCommonIssues({
                params: {
                    apartment_id: actionItemAnalytic.id,
                    upper_bound_date: upperBoundDate,
                    lower_bound_date: lowerBoundDate,
                },
            })
        } else {
            getActionItemDetailAnalytics({
                params: {
                    apartment_id: actionItemAnalytic.id,
                    upper_bound_date: upperBoundDate,
                    lower_bound_date: lowerBoundDate,
                },
            })
        }
    }

    const { lowerBoundDate, upperBoundDate } = getDates()

    return (
        <Container
            style={{
                marginTop: theme.spacing(1),
                flex: 1,
                flexDirection: 'column',
                height: 'calc(100vh - 80px)',
                minHeight: '600px',
                display: 'flex',
            }}
        >
            <Container
                style={{
                    flexShrink: 0,
                    padding: theme.spacing(1),
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Container
                    style={{ alignItems: 'center', gap: theme.spacing(2) }}
                >
                    <SearchField
                        value={searchText}
                        variant="outlined"
                        onChange={(e) => {
                            setSearchText(e.target.value)
                        }}
                        size="small"
                        placeholder="Search"
                        style={{ maxWidth: 300 }}
                    />

                    <DateFilterButtons
                        dateFilter={dateFilter}
                        setDateFilter={setDateFilter}
                        dateRange={dateRange}
                        setStartDate={setStartDate}
                        setEndDate={setEndDate}
                        onApply={() => {
                            getActionItemAnalytics({
                                organization: organization ?? undefined,
                                workorder_type: workorderType,
                                start_date: dateRange.startDate
                                    ? dateRange.startDate.toISOString()
                                    : undefined,
                                end_date: dateRange.endDate
                                    ? dateRange.endDate.toISOString()
                                    : undefined,
                            }).then((actionItemAnalytics) => {
                                setOrderBy(CurrentOpenCountDESC)
                                const sortedAnalytics = [
                                    ...actionItemAnalytics,
                                ].sort(
                                    (a, b) =>
                                        b.current_open_count -
                                        a.current_open_count,
                                )
                                setActionItemAnalytics(sortedAnalytics)
                            })
                        }}
                        excludeAll
                    />
                </Container>

                <FormControl
                    variant="outlined"
                    size="small"
                    style={{ minWidth: 200 }}
                >
                    <InputLabel>Filter by Tags</InputLabel>
                    <Select
                        multiple
                        value={selectedTagIds}
                        onChange={(e) =>
                            setSelectedTagIds(e.target.value as number[])
                        }
                        label="Filter by Tags"
                        renderValue={(selected) => (
                            <Box
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 4,
                                }}
                            >
                                {(selected as number[]).map((tagId) => {
                                    const tag = userTags.find(
                                        (t) => t.id === tagId,
                                    )
                                    return tag ? (
                                        <Chip
                                            key={tagId}
                                            label={tag.name}
                                            size="small"
                                            style={{
                                                backgroundColor: tag.color,
                                                color: 'white',
                                            }}
                                        />
                                    ) : null
                                })}
                            </Box>
                        )}
                        disabled={tagsLoading}
                    >
                        {userTags.map((tag) => (
                            <MenuItem key={tag.id} value={tag.id}>
                                <Chip
                                    label={tag.name}
                                    size="small"
                                    style={{
                                        backgroundColor: tag.color,
                                        color: 'white',
                                        marginRight: theme.spacing(1),
                                    }}
                                />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Container>

            <Container
                style={{
                    padding: theme.spacing(0, 1),
                    marginTop: theme.spacing(1),
                    flexShrink: 0,
                    minHeight: '48px',
                }}
            >
                <Container
                    style={{
                        ...headCellStyle,
                    }}
                    onClick={() => {
                        const newRows = [...actionItemAnalytics]
                        if (orderBy !== ApartmentASC) {
                            newRows.sort((RowA, RowB) =>
                                RowA.apartment_name.localeCompare(
                                    RowB.apartment_name,
                                ),
                            )
                            setActionItemAnalytics(newRows)
                            setOrderBy(ApartmentASC)
                        } else {
                            setActionItemAnalytics(newRows.reverse())
                            setOrderBy(ApartmentDESC)
                        }
                    }}
                >
                    <TableSortLabel
                        active={
                            orderBy === ApartmentASC ||
                            orderBy === ApartmentDESC
                        }
                        direction={orderBy === ApartmentASC ? 'asc' : 'desc'}
                    >
                        Location
                    </TableSortLabel>
                </Container>

                <Container
                    style={{
                        ...headCellStyle,
                        alignItems: 'center',
                    }}
                >
                    Tag
                </Container>

                <Container
                    style={{
                        ...headCellStyle,
                    }}
                    onClick={() => {
                        const newRows = [...actionItemAnalytics]
                        if (orderBy !== AvgCompletedPerDayASC) {
                            newRows.sort(
                                (a, b) =>
                                    a.avg_completed_per_day -
                                    b.avg_completed_per_day,
                            )
                            setActionItemAnalytics(newRows)
                            setOrderBy(AvgCompletedPerDayASC)
                        } else {
                            setActionItemAnalytics(newRows.reverse())
                            setOrderBy(AvgCompletedPerDayDESC)
                        }
                    }}
                >
                    <TableSortLabel
                        active={
                            orderBy === AvgCompletedPerDayASC ||
                            orderBy === AvgCompletedPerDayDESC
                        }
                        direction={
                            orderBy === AvgCompletedPerDayASC ? 'asc' : 'desc'
                        }
                    >
                        Avg Completed/Day
                    </TableSortLabel>
                </Container>

                <Container
                    style={{
                        ...headCellStyle,
                    }}
                    onClick={() => {
                        const newRows = [...actionItemAnalytics]
                        if (orderBy !== AvgDaysToCompleteASC) {
                            newRows.sort(
                                (a, b) =>
                                    a.avg_days_to_complete -
                                    b.avg_days_to_complete,
                            )
                            setActionItemAnalytics(newRows)
                            setOrderBy(AvgDaysToCompleteASC)
                        } else {
                            setActionItemAnalytics(newRows.reverse())
                            setOrderBy(AvgDaysToCompleteDESC)
                        }
                    }}
                >
                    <TableSortLabel
                        active={
                            orderBy === AvgDaysToCompleteASC ||
                            orderBy === AvgDaysToCompleteDESC
                        }
                        direction={
                            orderBy === AvgDaysToCompleteASC ? 'asc' : 'desc'
                        }
                    >
                        Avg Days to Complete
                    </TableSortLabel>
                </Container>

                <Container
                    style={{
                        ...headCellStyle,
                    }}
                    onClick={() => {
                        const newRows = [...actionItemAnalytics]
                        if (orderBy !== AvgCreatedPerDayASC) {
                            newRows.sort(
                                (a, b) =>
                                    a.avg_created_per_day -
                                    b.avg_created_per_day,
                            )
                            setActionItemAnalytics(newRows)
                            setOrderBy(AvgCreatedPerDayASC)
                        } else {
                            setActionItemAnalytics(newRows.reverse())
                            setOrderBy(AvgCreatedPerDayDESC)
                        }
                    }}
                >
                    <TableSortLabel
                        active={
                            orderBy === AvgCreatedPerDayASC ||
                            orderBy === AvgCreatedPerDayDESC
                        }
                        direction={
                            orderBy === AvgCreatedPerDayASC ? 'asc' : 'desc'
                        }
                    >
                        Avg Created/Day
                    </TableSortLabel>
                </Container>

                <Container
                    style={{
                        ...headCellStyle,
                    }}
                    onClick={() => {
                        const newRows = [...actionItemAnalytics]
                        if (orderBy !== TotalCreatedCountASC) {
                            newRows.sort(
                                (a, b) =>
                                    a.total_created_count -
                                    b.total_created_count,
                            )
                            setActionItemAnalytics(newRows)
                            setOrderBy(TotalCreatedCountASC)
                        } else {
                            setActionItemAnalytics(newRows.reverse())
                            setOrderBy(TotalCreatedCountDESC)
                        }
                    }}
                >
                    <TableSortLabel
                        active={
                            orderBy === TotalCreatedCountASC ||
                            orderBy === TotalCreatedCountDESC
                        }
                        direction={
                            orderBy === TotalCreatedCountASC ? 'asc' : 'desc'
                        }
                    >
                        Total Created
                    </TableSortLabel>
                </Container>

                <Container
                    style={{
                        ...headCellStyle,
                    }}
                    onClick={() => {
                        const newRows = [...actionItemAnalytics]
                        if (orderBy !== TotalCompletedCountASC) {
                            newRows.sort(
                                (a, b) =>
                                    a.total_completed_count -
                                    b.total_completed_count,
                            )
                            setActionItemAnalytics(newRows)
                            setOrderBy(TotalCompletedCountASC)
                        } else {
                            setActionItemAnalytics(newRows.reverse())
                            setOrderBy(TotalCompletedCountDESC)
                        }
                    }}
                >
                    <TableSortLabel
                        active={
                            orderBy === TotalCompletedCountASC ||
                            orderBy === TotalCompletedCountDESC
                        }
                        direction={
                            orderBy === TotalCompletedCountASC ? 'asc' : 'desc'
                        }
                    >
                        Total Completed
                    </TableSortLabel>
                </Container>
                <Container
                    style={{
                        ...headCellStyle,
                    }}
                    onClick={() => {
                        const newRows = [...actionItemAnalytics]
                        if (orderBy !== CurrentOpenCountASC) {
                            newRows.sort(
                                (a, b) =>
                                    a.current_open_count - b.current_open_count,
                            )
                            setActionItemAnalytics(newRows)
                            setOrderBy(CurrentOpenCountASC)
                        } else {
                            setActionItemAnalytics(newRows.reverse())
                            setOrderBy(CurrentOpenCountDESC)
                        }
                    }}
                >
                    <TableSortLabel
                        active={
                            orderBy === CurrentOpenCountASC ||
                            orderBy === CurrentOpenCountDESC
                        }
                        direction={
                            orderBy === CurrentOpenCountASC ? 'asc' : 'desc'
                        }
                    >
                        Open
                    </TableSortLabel>
                </Container>

                <Container
                    style={{
                        ...headCellStyle,
                    }}
                    onClick={() => {
                        const newRows = [...actionItemAnalytics]
                        if (orderBy !== LongOpenCountASC) {
                            newRows.sort(
                                (a, b) => a.long_open_count - b.long_open_count,
                            )
                            setActionItemAnalytics(newRows)
                            setOrderBy(LongOpenCountASC)
                        } else {
                            setActionItemAnalytics(newRows.reverse())
                            setOrderBy(LongOpenCountDESC)
                        }
                    }}
                >
                    <TableSortLabel
                        active={
                            orderBy === LongOpenCountASC ||
                            orderBy === LongOpenCountDESC
                        }
                        direction={
                            orderBy === LongOpenCountASC ? 'asc' : 'desc'
                        }
                    >
                        Open Over 10 Days
                    </TableSortLabel>
                </Container>
            </Container>

            <Container
                style={{
                    overflowY: 'auto',
                    flex: 1,
                    minHeight: '200px',
                    maxHeight: 'calc(100vh - 280px)',
                }}
                direction="column"
            >
                {loadingState.mainPage ? (
                    <Container
                        style={{
                            alignItems: 'center',
                            justifyContent: 'center',
                            flex: 1,
                        }}
                    >
                        <CircularProgress size={100} />
                    </Container>
                ) : (
                    pageData.map((actionItemAnalytic, idx) => {
                        const columns: (string | number | React.ReactNode)[] = [
                            actionItemAnalytic.apartment_name,
                            renderTags(actionItemAnalytic),
                            Number(
                                actionItemAnalytic.avg_completed_per_day.toFixed(
                                    2,
                                ),
                            ),
                            Number(
                                actionItemAnalytic.avg_days_to_complete.toFixed(
                                    2,
                                ),
                            ),
                            Number(
                                actionItemAnalytic.avg_created_per_day.toFixed(
                                    2,
                                ),
                            ),
                            actionItemAnalytic.total_created_count,
                            actionItemAnalytic.total_completed_count,
                            actionItemAnalytic.current_open_count,
                            actionItemAnalytic.long_open_count,
                        ]

                        return (
                            <WorkspaceProjectsRow
                                key={`ACTION_ITEM_APARTMENT_${actionItemAnalytic.id}`}
                                columns={columns}
                                theme={theme}
                                onClickRow={() =>
                                    onClickRow(actionItemAnalytic)
                                }
                            />
                        )
                    })
                )}
            </Container>

            <Container
                style={{
                    ...typographyStyle(theme.typography.subtitle1),
                    borderTop: `1px solid ${theme.palette.grey[400]}`,
                    padding: theme.spacing(1),
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexShrink: 0,
                    minHeight: '56px',
                    backgroundColor: theme.palette.background.paper,
                    position: 'sticky',
                    bottom: 0,
                }}
            >
                <Pagination
                    count={numPages}
                    page={page}
                    onChange={(_: unknown, newPage: number) => {
                        setPage(newPage)
                    }}
                />
                <span>
                    {start} - {end} of {actionItemAnalytics?.length ?? 0}
                </span>
            </Container>

            {workorderType === WorkorderType.SERVICE_REQUEST ? (
                <ServiceRequestAnalyticModal
                    open={openDetailModal}
                    loadingState={loadingState}
                    serviceRequestDetailAnalytics={
                        serviceRequestDetailAnalytics
                    }
                    activeActionItemAnalytic={activeActionItemAnalytic}
                    startDate={lowerBoundDate}
                    endDate={upperBoundDate}
                    commonIssues={serviceRequestCommonIssues}
                    handleClose={() => {
                        setOpenDetailModal(false)
                    }}
                />
            ) : (
                <ActionItemAnalyticModal
                    open={openDetailModal}
                    loadingState={loadingState}
                    actionItemDetailAnalytics={actionItemDetailAnalytics}
                    activeActionItemAnalytic={activeActionItemAnalytic}
                    startDate={lowerBoundDate}
                    endDate={upperBoundDate}
                    handleClose={() => {
                        setOpenDetailModal(false)
                    }}
                />
            )}
        </Container>
    )
}

const ApartmentASC = 'ApartmentASC'
const ApartmentDESC = 'ApartmentDESC'
const AvgCompletedPerDayASC = 'AvgCompletedPerDayASC'
const AvgCompletedPerDayDESC = 'AvgCompletedPerDayDESC'
const AvgDaysToCompleteASC = 'AvgDaysToCompleteASC'
const AvgDaysToCompleteDESC = 'AvgDaysToCompleteDESC'
const AvgCreatedPerDayASC = 'AvgCreatedPerDayASC'
const AvgCreatedPerDayDESC = 'AvgCreatedPerDayDESC'
const CurrentOpenCountASC = 'CurrentOpenCountASC'
const CurrentOpenCountDESC = 'CurrentOpenCountDESC'
const TotalCreatedCountASC = 'TotalCreatedCountASC'
const TotalCreatedCountDESC = 'TotalCreatedCountDESC'
const TotalCompletedCountASC = 'TotalCompletedCountASC'
const TotalCompletedCountDESC = 'TotalCompletedCountDESC'
const LongOpenCountASC = 'LongOpenCountASC'
const LongOpenCountDESC = 'LongOpenCountDESC'
